import { useMemo } from 'react'
import { useMovementBulkLoad } from '@/hooks/useMovementBulkLoad'
import {
    buildPathPointDescription,
    calculateRegionFromCoordinates,
    filterMarkersByDistance,
    getMinDistanceMeters,
} from '@/lib/map-utils'
import { getMovementIcon, getMovementInfo } from '@/lib/movement-utils'
import type { MapCoordinate, PathMarkerData, UserPathData } from '@/types/mapData'

interface UseTravelMovementOptions {
    orgSlug?: string
    userId: string
    clockIn: string
    clockOut: string
    userColor: string
    userName: string
    enabled?: boolean
}

export function useTravelMovement({
    orgSlug,
    userId,
    clockIn,
    clockOut,
    userColor,
    userName,
    enabled = true,
}: UseTravelMovementOptions) {
    const { data: allMovements, isLoading } = useMovementBulkLoad({
        orgSlug,
        startDate: clockIn,
        endDate: clockOut,
        enabled,
    })

    const userMovements = useMemo(() => {
        if (!allMovements) return []
        return allMovements.filter(m => m.user === userId)
    }, [allMovements, userId])

    const region = useMemo(() => calculateRegionFromCoordinates(userMovements), [userMovements])

    const path = useMemo((): UserPathData | null => {
        if (userMovements.length === 0) return null

        const coordinates: MapCoordinate[] = userMovements.map(p => ({
            latitude: p.latitude,
            longitude: p.longitude,
        }))

        const allMarkers: PathMarkerData[] = userMovements.map(point => {
            const icon = getMovementIcon(point.movement_type, point.is_moving, point.speed)
            const { rotation } = getMovementInfo(
                point.is_moving,
                point.movement_type,
                point.heading,
                point.speed
            )
            const isNavigationIcon = icon === 'navigation'

            return {
                id: point.id,
                coordinate: {
                    latitude: point.latitude,
                    longitude: point.longitude,
                },
                userName,
                userColor,
                icon,
                rotation,
                shouldFlip: !isNavigationIcon,
                description: buildPathPointDescription(point),
            }
        })

        const minDistance = getMinDistanceMeters(region.latitudeDelta)
        const pathMarkers = filterMarkersByDistance(allMarkers, minDistance)

        const timestamps = userMovements.map(p => p.timestamp)

        return {
            userId,
            userColor,
            coordinates,
            pathMarkers,
            timestamps,
        }
    }, [userMovements, userName, userColor, userId, region.latitudeDelta])

    return {
        path,
        region,
        isLoading,
        isEmpty: !isLoading && userMovements.length === 0,
    }
}
